import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import sortBy from 'lodash/sortBy';

import { DetailHistoryValidationComponent } from '../../../shared/components/detail-history-validation/detail-history-validation.component';
import { EnhancedLoadingService } from '../../../shared/services/enhanced-loading.service';
import { SmartOrpcClientService } from '../../../shared/services/smart-orpc-client.service';
import { DetailStatusHistoryComponent } from './detail-status-history/detail-status-history.component';
import type { contractOutput } from '#backend/contract';

type BatchHistoriesSafrasRequest = contractOutput['historiesSafras']['byId'];

@Component({
  selector: 'app-histories-safras-detail',
  standalone: true,
  imports: [
    DetailStatusHistoryComponent,
    MatIconModule,
    MatButtonModule,
    DetailHistoryValidationComponent,
  ],
  templateUrl: './histories-safras-detail.component.html',
  styleUrl: './histories-safras-detail.component.scss',
})
export class HistoriesSafrasDetailComponent implements OnInit {
  private orpc = inject(SmartOrpcClientService);
  private loadingService = inject(EnhancedLoadingService);
  private snackBar = inject(MatSnackBar);
  id: string | null = null;
  data: BatchHistoriesSafrasRequest | null = null;

  get canHistoriesUpload() {
    return (
      // Arquivo aprovado e status analisando, mas ainda não enviou DBF/FGB
      (this.getLastFile()?.status.id === 'APPROVED_BY_MANUAL_VALIDATION' &&
        this.data?.status.code === 'ANALYZING' &&
        this.data?.isSentReports === false) ||
      // Com inconsistência detectada durante processamento
      (this.data?.isInconsistency === true &&
        this.data?.status.code === 'ANALYZING' &&
        this.data?.isSentReports === true)
    );
  }
  get canApproveOrReject() {
    return (
      this.getLastFile()?.status.id == 'MANUAL_VALIDATION_IN_PROGRESS' &&
      this.data?.status.code === 'VALIDATING_INPUT'
    );
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
  ) {}
  ngOnInit() {
    this.id = this.route.snapshot.paramMap.get('id');
    this.route.queryParamMap.subscribe(async () => {
      this.loadData();
    });
  }

  async fetchRequestById(id: string) {
    return await this.orpc.client.historiesSafras.byId(Number(id));
  }

  async loadData() {
    if (this.id) {
      this.data = await this.fetchRequestById(this.id);
      if (this.data?.fileValidations) {
        this.data.fileValidations = sortBy(
          this.data?.fileValidations,
          (v) => v.createdDate,
        );
      }
    }
  }

  private getLastFile() {
    return this.data?.fileValidations?.slice(-1)[0];
  }

  alert(message: string) {
    this.snackBar.open(message, 'Fechar', {
      duration: 5000,
    });
  }

  prompt(message: string, defaultValue: string) {
    return window.prompt(message, defaultValue);
  }

  approve() {
    const lastFileValidation = this.getLastFile();
    if (lastFileValidation?.id == null) {
      throw new Error('Houve um erro ao aprovar a validação do arquivo.');
    }

    const confirmApprove = confirm(
      `Tem certeza que deseja aprovar a validação do arquivo ${lastFileValidation.id}?`,
    );
    if (confirmApprove) {
      this.loadingService.show();
      this.orpc.client.historiesSafras.fileValidation
        .approve({
          fileValidationId: lastFileValidation.id,
        })
        .then(() => {
          this.alert(
            `Validação do arquivo ${lastFileValidation.id} aprovada com sucesso!`,
          );
          // Recarrega os dados
          this.loadData();
        })
        .finally(() => {
          this.loadingService.hide();
        });
    }
  }

  reject() {
    const lastFileValidation = this.getLastFile();
    if (lastFileValidation?.id == null) {
      throw new Error('Houve um erro ao rejeitar a validação do arquivo.');
    }

    // const reason = this.prompt(
    //   'Digite o motivo da rejeição da validação:',
    //   'Motivo não informado',
    // );

    this.loadingService.show();
    this.orpc.client.historiesSafras.fileValidation
      .reject({
        fileValidationId: lastFileValidation.id,
      })
      .then(() => {
        this.alert(
          `Validação do arquivo ${lastFileValidation.id} rejeitada com sucesso!`,
        );
        // Recarrega os dados
        this.loadData();
      })
      .finally(() => {
        this.loadingService.hide();
      });
  }
  navigateToReportUpload() {
    this.router.navigate([
      '/histories-safras/adm/detail/' + this.id + '/upload-report',
    ]);
  }
}
